import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  getNotificationCount,
  getNotifications,
  loadMoreNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  notificationAsyncActions,
  resetNotifications
} from 'redux/pageAsyncActions';
import {INotification} from 'redux/notifications/interfaces';

import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';

import NotFoundImg from 'assets/image/common/notFound.png';
import {NotificationItem} from './NotificationItem';
import {TranslationKeys} from '../../utils/constants/routeConstants';
import {useGetTranslations} from '../../utils/hooks/useGetTranslations';

import './styles.scss';

export const Notifications: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const {notifications, page, notificationCount} = useSelector((state: IStore) => state.notifications);
  const {loading, errors} = useSelector((state: IStore) => state.status);

  useGetTranslations({translationKey: TranslationKeys.Notifications});

  const isLoading = loading.includes(getNotifications);
  const isLoadingMore = loading.includes(loadMoreNotifications);
  const hasError = Boolean(errors[getNotifications]);

  const hasMore = page !== null && notifications.length < page.totalElements;

  const fetchPage = useCallback((offset: number, append: boolean) => {
    dispatch(notificationAsyncActions[getNotifications]({
      indRead: true,
      offset,
      append
    }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(notificationAsyncActions[resetNotifications]());
    dispatch(notificationAsyncActions[getNotificationCount]());
    fetchPage(0, false);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchPage(notifications.length, true);
    }
  }, [fetchPage, hasMore, isLoadingMore, notifications.length]);

  const handleMarkRead = useCallback(
    (notification: INotification) => {
      dispatch(notificationAsyncActions[markNotificationAsRead](notification.id));
    },
    [dispatch]
  );

  const handleMarkAllRead = useCallback(() => {
    dispatch(notificationAsyncActions[markAllNotificationsAsRead]());
  }, [dispatch]);

  function renderList() {
    if (isLoading) {
      return (
        <div className="notifications__loader-wrap">
          <Loader nested/>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="notifications__error">
          <p>
            <FormattedMessage
              id="gritx.notifications.error"
              defaultMessage="Failed to load notifications."
            />
          </p>
          <button
            type="button"
            className="notifications__retry-btn"
            onClick={() => fetchPage(0, false)}
          >
            <FormattedMessage id="gritx.notifications.retry" defaultMessage="Try again"/>
          </button>
        </div>
      );
    }

    if (notifications?.length === 0) {
      return (
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.notifications.emptyAll',
            defaultMessage: 'No notifications yet'
          })}
          image={NotFoundImg}
        />
      );
    }

    return (
      <>

        {page && (
          <p className="notifications__count">
            <FormattedMessage
              id="gritx.notifications.count"
              defaultMessage="{displayed} of {total}"
              values={{displayed: notifications.length, total: page.totalElements}}
            />
          </p>
        )}
        <ul className="notifications__list">
          {notifications?.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkRead}
            />
          ))}
        </ul>

        {hasMore && (
          <div className="notifications__load-more">
            <button
              type="button"
              className="notifications__load-more-btn"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore
                ? <FormattedMessage id="gritx.notifications.loading" defaultMessage="Loading…"/>
                : <FormattedMessage id="gritx.notifications.loadMore" defaultMessage="Load more"/>
              }
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <section className="notifications container">
      <div className="notifications__header">
        <h1 className="notifications__title">
          <FormattedMessage id="gritx.notifications.title" defaultMessage="Notifications"/>
        </h1>
        {notificationCount > 0 && (
          <button
            type="button"
            className="notifications__mark-all-btn"
            onClick={handleMarkAllRead}
          >
            <FormattedMessage id="gritx.notifications.markAllRead" defaultMessage="Mark all as read"/>
          </button>
        )}
      </div>
      {renderList()}
    </section>
  );
};
